import { css } from 'aphrodite';
import * as React from 'react';
import { ISupportGitHubPanel } from '../../../models/Support/ISupportGitHub';
import { useSupportGitHubQuery } from '../../../queries/Support/useSupportGitHub';
import { getApiErrorMessage, isNotFoundError } from '../../../utils/Support/supportHelpers';
import { SparklesIcon } from '../../../web/components/svgs/icons/SparklesIcon';
import { Button } from '../../../web/components-v2/Button';
import { GitHubInvestigationCard } from './GitHubInvestigationCard';
import { groupIssuesByKind } from './githubPanelPresentation';
import { GitHubEngineeringCard, GitHubLinkedIssueCard } from './GitHubRecordCard';
import { GitHubGlyph, RefreshGlyph } from './inboxIcons';
import { styles } from './styles';

interface GitHubPanelProps {
	ticketId: string;
	/** True while an AI reply draft from an investigation is waiting in the conversation. */
	hasPendingAiDraft?: boolean;
	onCreateIssue?: () => void;
	onLinkIssue?: () => void;
}

/**
 * GitHub records for a ticket, grouped by what kind of record they are and — for managed
 * investigations — led by where each one sits in its lifecycle. Renders nothing at all where the
 * integration is unavailable and the ticket has no records, so only the sidebars that can use it
 * carry it.
 *
 * State only ever advances when the agent asks for it: the panel refresh re-reads stored workflow
 * results, and each card's Refresh re-reads that issue from GitHub. Nothing polls.
 */
export const GitHubPanel: React.FC<GitHubPanelProps> = ({
	ticketId,
	hasPendingAiDraft,
	onCreateIssue,
	onLinkIssue,
}) => {
	const query = useSupportGitHubQuery(ticketId);
	const issues = query.data?.issues ?? [];
	const legacyIssues = query.data?.legacyIssues ?? [];
	const hasAnyLinks = issues.length > 0 || legacyIssues.length > 0;
	const enabled = query.data?.enabled === true;

	// A 404 means the integration does not exist in this environment at all, and until the first load
	// settles there is nothing yet to say — a panel that appeared and then vanished would read as a
	// glitch. Neither state renders anything.
	if (query.isLoading || (query.isError && isNotFoundError(query.error))) {
		return null;
	}

	// The panel earns its place when the agent can act on GitHub, when the ticket already carries
	// records to read, or when the load failed and that is worth reporting. Without one of those, a
	// mailbox with no repositories attached would leave a dead panel in every ticket sidebar.
	// Existing records keep the panel even while the integration is switched off, so turning it off
	// never hides GitHub context a ticket has already accumulated.
	if (!enabled && !hasAnyLinks && !query.isError) {
		return null;
	}

	const sections = groupIssuesByKind(issues);
	const panelStatusLabel = query.isError ? 'Unavailable' : query.isFetching ? 'Refreshing saved state…' : undefined;

	return (
		<section className={css(styles.githubPanel)} aria-label='GitHub issue integration'>
			<div className={css(styles.githubPanelHeader)}>
				<span className={css(styles.githubPanelIcon)}>
					<GitHubGlyph size={22} />
				</span>
				<div className={css(styles.githubPanelHeading)}>
					<div className={css(styles.githubPanelTitle)}>GitHub</div>
				</div>
				{panelStatusLabel && (
					<span
						className={css(
							styles.githubPanelStatus,
							query.isError ? styles.githubStatusError : styles.githubStatusSyncing
						)}
					>
						<span className={css(styles.githubPanelStatusDot)} />
						{panelStatusLabel}
					</span>
				)}
				<Button
					variant='outline'
					size='icon'
					aria-label='Check for updates'
					title='Check for updates'
					onClick={() => query.refetch()}
					disabled={query.isFetching}
				>
					<RefreshGlyph />
				</Button>
			</div>

			{query.isError && (
				<div className={css(styles.githubPanelError)} role='alert' aria-live='polite'>
					{getApiErrorMessage(query.error, 'Failed to load GitHub panel data.')}
				</div>
			)}

			{!query.isError && !hasAnyLinks && (
				<div className={css(styles.githubPanelPitch)}>
					<span className={css(styles.githubPanelPitchTitle)}>Start with a GitHub investigation</span>
					<span className={css(styles.githubPanelEmpty)}>
						The configured repository workflow reads the ticket and returns a root cause with a customer-ready draft
						reply. No GitHub issues linked yet.
					</span>
				</div>
			)}

			{hasAnyLinks && (
				<div className={css(styles.githubPanelSections)}>
					{sections.investigations.length > 0 && (
						<PanelSection label='Investigation'>
							{sections.investigations.map(issue => (
								<GitHubInvestigationCard key={issue.id} ticketId={ticketId} issue={issue} enabled={enabled} />
							))}
							{hasPendingAiDraft && <AiDraftStrip />}
						</PanelSection>
					)}

					{sections.engineering.length > 0 && (
						<PanelSection label='Engineering'>
							{sections.engineering.map(issue => (
								<GitHubEngineeringCard
									key={issue.id}
									ticketId={ticketId}
									issue={issue}
									issues={issues}
									enabled={enabled}
								/>
							))}
						</PanelSection>
					)}

					{(sections.other.length > 0 || legacyIssues.length > 0) && (
						<PanelSection label='Other issues'>
							{sections.other.map(issue => (
								<GitHubLinkedIssueCard key={issue.id} ticketId={ticketId} issue={issue} enabled={enabled} />
							))}
							{legacyIssues.length > 0 && <LegacyLinks issues={legacyIssues} />}
						</PanelSection>
					)}
				</div>
			)}

			<div className={css(styles.githubPanelActions)}>
				{onCreateIssue && (
					<Button variant='primary' size='sm' onClick={onCreateIssue} disabled={!enabled}>
						Create &amp; Investigate
					</Button>
				)}
				{onLinkIssue && (
					<Button variant='outline' size='sm' onClick={onLinkIssue} disabled={!enabled}>
						Link Existing
					</Button>
				)}
			</div>

			{!query.isError && !enabled && (
				<div className={css(styles.githubPanelNotice)}>
					GitHub is disabled or this mailbox has no attached repositories, so these records are read-only. Add
					repositories in mailbox settings.
				</div>
			)}
		</section>
	);
};

const PanelSection: React.FC<React.PropsWithChildren<{ label: string }>> = ({ label, children }) => (
	<div>
		<div className={css(styles.githubSectionHeader)}>
			<span className={css(styles.githubSectionLabel)}>{label}</span>
			<span className={css(styles.githubSectionRule)} />
		</div>
		<div className={css(styles.githubSectionBody)}>{children}</div>
	</div>
);

const AiDraftStrip: React.FC = () => (
	<div className={css(styles.githubDraftStrip)}>
		<span className={css(styles.githubDraftStripIcon)}>
			<SparklesIcon height={12} width={12} />
		</span>
		<span className={css(styles.githubDraftStripText)}>Reply draft ready in the conversation</span>
	</div>
);

const LegacyLinks: React.FC<{ issues: ISupportGitHubPanel['legacyIssues'] }> = ({ issues }) => (
	<div className={css(styles.githubLegacyList)}>
		{issues.map(issue => (
			<a
				key={issue.url}
				href={issue.url}
				target='_blank'
				rel='noopener noreferrer'
				className={css(styles.githubLegacyLink)}
			>
				{issue.repo}#{issue.number}
			</a>
		))}
	</div>
);
